import { useEffect, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { toast, Toaster } from "sonner";
import { ArrowDown, ArrowUpRight, Mail, MapPin, Star } from "lucide-react";
import axios from "axios";
import { translations, detectLanguage } from "@/i18n";
import { IMAGES } from "@/assets";
import useLenis from "@/useLenis";
import InteractivePhoto from "@/components/InteractivePhoto";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ---------- helpers ---------- */
const MaskLine = ({ children, delay = 0, className = "" }) => (
  <span className={`mask-line ${className}`}>
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
      style={{ display: "block", willChange: "transform" }}
    >
      {children}
    </motion.span>
  </span>
);

const Reveal = ({ children, delay = 0, y = 30, once = true, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once, amount: 0.2 }}
    transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

/* ---------- Header ---------- */
const Header = ({ lang, setLang }) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const t = translations[lang];
  const toggle = () => {
    const next = lang === "it" ? "en" : "it";
    setLang(next);
    window.localStorage.setItem("asroma_lang", next);
  };
  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "backdrop-blur-md bg-paper/75 border-b border-ink/10" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3 group" data-testid="brand-mark">
          <span className="inline-block w-2 h-2 rounded-full bg-bordeaux" />
          <span className="font-mono-tiny text-ink">AS Roma · 1982 / 83</span>
        </a>
        <nav className="hidden md:flex items-center gap-8 font-mono-tiny text-ink/70">
          <a href="#manifesto" className="hover:text-bordeaux transition-colors" data-testid="nav-manifesto">{t.nav.manifesto}</a>
          <a href="#gallery" className="hover:text-bordeaux transition-colors" data-testid="nav-gallery">{t.nav.gallery}</a>
          <a href="#interactive" className="hover:text-bordeaux transition-colors" data-testid="nav-interactive">{lang === "it" ? "Mappa" : "Map"}</a>
          <a href="#signatures" className="hover:text-bordeaux transition-colors" data-testid="nav-signatures">{t.nav.story}</a>
          <a href="#contact" className="hover:text-bordeaux transition-colors" data-testid="nav-contact">{t.nav.contact}</a>
        </nav>
        <button
          onClick={toggle}
          className="relative font-mono-tiny text-ink hover:text-bordeaux transition-colors flex items-center gap-2"
          data-testid="lang-toggle"
        >
          <span className={lang === "it" ? "text-bordeaux" : "text-ink/40"}>IT</span>
          <span className="w-6 h-px bg-ink/30" />
          <span className={lang === "en" ? "text-bordeaux" : "text-ink/40"}>EN</span>
        </button>
      </div>
    </motion.header>
  );
};

/* ---------- Hero ---------- */
const Hero = ({ lang }) => {
  const t = translations[lang].hero;
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 800], [0, 60]);
  return (
    <section id="top" className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden" data-testid="hero-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        {/* eyebrow */}
        <div className="flex items-center gap-4 mb-10 md:mb-14">
          <motion.span
            initial={{ scaleX: 0 }} animate={{ scaleX: 1 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            style={{ transformOrigin: "left" }}
            className="block h-px w-16 bg-bordeaux"
          />
          <MaskLine delay={0.1} className="font-mono-tiny text-ink/70">{t.eyebrow}</MaskLine>
        </div>

        {/* Kinetic title */}
        <h1 className="font-display font-light text-ink tracking-tight text-[13vw] md:text-[10vw] lg:text-[8.6vw] leading-[0.92]">
          <MaskLine delay={0.25}>{t.title1}</MaskLine>
          <MaskLine delay={0.4} className="italic text-bordeaux">{t.title2}</MaskLine>
          <MaskLine delay={0.55}>{t.title3}</MaskLine>
        </h1>

        {/* Bottom row */}
        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <Reveal delay={0.9} className="md:col-span-5">
            <p className="text-ink/80 text-lg md:text-xl leading-relaxed max-w-md">{t.lede}</p>
          </Reveal>
          <div className="md:col-span-4 md:col-start-9 flex md:justify-end">
            <Reveal delay={1.05}>
              <a
                href="#contact"
                data-testid="hero-cta"
                className="group inline-flex items-center gap-3 bg-bordeaux text-paper hover:bg-bordeaux-deep transition-colors px-7 py-4 rounded-full"
              >
                <span className="font-mono-tiny">{t.cta}</span>
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </Reveal>
          </div>
        </div>

        {/* Framed photograph with spotlight + parallax + hover-to-explore CTA */}
        <div className="relative mt-16 md:mt-24 group">
          <div className="absolute inset-0 spotlight pointer-events-none" />
          <motion.div
            style={{ y: imgY }}
            className="relative frame-shadow rounded-sm bg-paper2"
            data-testid="hero-image"
          >
            <motion.img
              src={IMAGES[0]}
              alt="AS Roma 1982/83 signed photograph"
              className="w-full h-auto block select-none"
              draggable={false}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.6, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            />
            {/* gold hairline */}
            <div className="absolute inset-3 md:inset-5 border border-gold/40 pointer-events-none" />

            {/* Hover-to-explore overlay CTA */}
            <a
              href="#interactive"
              data-testid="hero-explore-cta"
              className="absolute inset-0 flex items-end justify-center pb-8 md:pb-12 opacity-0 group-hover:opacity-100 transition-opacity duration-500 focus:outline-none focus-visible:opacity-100"
              aria-label={lang === "it" ? "Esplora la mappa interattiva" : "Explore the interactive map"}
            >
              <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-ink/70 via-ink/30 to-transparent pointer-events-none" />
              <motion.div
                initial={false}
                className="relative flex items-center gap-3 px-6 py-3 bg-paper text-ink rounded-full shadow-xl border border-gold/50 translate-y-4 group-hover:translate-y-0 transition-transform duration-500"
              >
                <span className="font-mono-tiny">{lang === "it" ? "Esplora la mappa interattiva" : "Explore the interactive map"}</span>
                <ArrowUpRight className="w-4 h-4 text-bordeaux" />
              </motion.div>
            </a>
          </motion.div>

          {/* Meta strip */}
          <div className="mt-6 flex flex-wrap items-center justify-between gap-4 font-mono-tiny text-ink/60">
            <span className="flex items-center gap-2"><MapPin className="w-3 h-3" /> Roma, Italia</span>
            <span>Foto Crescenzi — 1983</span>
            <span className="flex items-center gap-2"><Star className="w-3 h-3 text-bordeaux" /> 1 / 1 · unico</span>
          </div>
        </div>

        {/* scroll cue */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 1 }}
          className="mt-14 flex items-center gap-3 text-ink/60 font-mono-tiny"
        >
          <motion.span
            animate={{ y: [0, 6, 0] }} transition={{ duration: 2, repeat: Infinity }}
            className="inline-flex"
          ><ArrowDown className="w-3 h-3" /></motion.span>
          {t.scroll}
        </motion.div>
      </div>
    </section>
  );
};

/* ---------- Marquee ---------- */
const MarqueeStrip = ({ lang }) => {
  const text = translations[lang].marquee;
  return (
    <section className="py-16 md:py-24 border-y border-ink/10 bg-paper2/40 overflow-hidden" data-testid="marquee-section">
      <Marquee speed={40} gradient={false} pauseOnHover={false}>
        <span className="font-display italic text-[14vw] md:text-[10vw] lg:text-[8vw] leading-none marquee-outline mr-16">
          {text}
        </span>
        <span className="font-display italic text-[14vw] md:text-[10vw] lg:text-[8vw] leading-none text-bordeaux mr-16">
          {text}
        </span>
      </Marquee>
    </section>
  );
};

/* ---------- Manifesto ---------- */
const Manifesto = ({ lang }) => {
  const t = translations[lang].manifesto;
  return (
    <section id="manifesto" className="py-24 md:py-40" data-testid="manifesto-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 mb-20 md:mb-28">
          <div className="md:col-span-3 font-mono-tiny text-bordeaux">
            <Reveal>{t.eyebrow}</Reveal>
          </div>
          <div className="md:col-span-8">
            <Reveal delay={0.05}>
              <h2 className="font-display text-4xl md:text-6xl lg:text-7xl text-ink leading-[0.95]">
                {t.title}
              </h2>
            </Reveal>
          </div>
        </div>

        <div className="space-y-24 md:space-y-32">
          {t.chapters.map((c, i) => (
            <div key={c.n} className="grid md:grid-cols-12 gap-6 md:gap-10 items-start" data-testid={`chapter-${c.n}`}>
              <Reveal className="md:col-span-3" delay={0}>
                <div className="font-display italic text-[18vw] md:text-[9vw] lg:text-[8vw] text-bordeaux/90 leading-none">
                  {c.n}
                </div>
              </Reveal>
              <Reveal className="md:col-span-4" delay={0.08}>
                <h3 className="font-display text-3xl md:text-5xl text-ink leading-tight">{c.title}</h3>
              </Reveal>
              <Reveal className="md:col-span-5 md:col-start-8" delay={0.16}>
                <p className="text-ink/80 text-base md:text-lg leading-relaxed">{c.body}</p>
                <div className="mt-6 h-px w-16 bg-gold" />
              </Reveal>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Gallery ---------- */
const Gallery = ({ lang }) => {
  const t = translations[lang].gallery;
  const layout = [
    { src: IMAGES[0], span: "md:col-span-8", ratio: "aspect-[4/3]" },
    { src: IMAGES[3], span: "md:col-span-4", ratio: "aspect-[3/4]" },
    { src: IMAGES[2], span: "md:col-span-4", ratio: "aspect-[4/3]" },
    { src: IMAGES[1], span: "md:col-span-4", ratio: "aspect-[4/3]" },
    { src: IMAGES[4], span: "md:col-span-4", ratio: "aspect-[4/3]" },
  ];
  const captions = t.captions;
  return (
    <section id="gallery" className="py-24 md:py-40 bg-paper2/50" data-testid="gallery-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-3 font-mono-tiny text-bordeaux">
            <Reveal>{t.eyebrow}</Reveal>
          </div>
          <div className="md:col-span-8">
            <Reveal delay={0.05}>
              <h2 className="font-display text-4xl md:text-6xl lg:text-7xl text-ink leading-[0.95]">{t.title}</h2>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          {layout.map((item, i) => (
            <Reveal key={i} delay={i * 0.05} className={`${item.span} col-span-1`}>
              <figure
                className={`group relative overflow-hidden ${item.ratio} bg-ink/5`}
                data-testid={`gallery-item-${i}`}
              >
                <motion.img
                  src={item.src}
                  alt={captions[i]}
                  className="absolute inset-0 w-full h-full object-cover cursor-view"
                  initial={{ scale: 1.05 }}
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                  draggable={false}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/50 via-ink/0 to-ink/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <figcaption className="absolute left-4 bottom-4 right-4 flex items-center justify-between text-paper font-mono-tiny opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <span>{captions[i]}</span>
                  <span>0{i + 1} / 05</span>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Signatures list ---------- */
const Signatures = ({ lang }) => {
  const t = translations[lang].signatures;
  return (
    <section id="signatures" className="py-24 md:py-40" data-testid="signatures-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-3 font-mono-tiny text-bordeaux">
            <Reveal>{t.eyebrow}</Reveal>
          </div>
          <div className="md:col-span-8">
            <Reveal delay={0.05}>
              <h2 className="font-display text-4xl md:text-6xl lg:text-7xl text-ink leading-[0.95]">{t.title}</h2>
            </Reveal>
          </div>
        </div>

        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <Reveal>
              <div className="font-mono-tiny text-ink/50 mb-6">Rosa · Squad</div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-8">
                {t.players.map((p, i) => (
                  <motion.li
                    key={p}
                    initial={{ opacity: 0, x: -12 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, amount: 0.5 }}
                    transition={{ duration: 0.6, delay: i * 0.03, ease: [0.22, 1, 0.36, 1] }}
                    className="font-display text-2xl md:text-3xl text-ink flex items-baseline gap-3"
                  >
                    <span className="font-body text-xs text-bordeaux/70">{String(i + 1).padStart(2, "0")}</span>
                    <span>{p}</span>
                  </motion.li>
                ))}
              </ul>
            </Reveal>
          </div>
          <div className="md:col-span-4 md:col-start-9">
            <Reveal delay={0.15}>
              <div className="font-mono-tiny text-ink/50 mb-6">Staff</div>
              <ul className="space-y-3">
                {t.staff.map((s) => (
                  <li key={s} className="font-display italic text-xl md:text-2xl text-bordeaux">{s}</li>
                ))}
              </ul>
              <div className="mt-8 h-px w-16 bg-gold" />
              <div className="mt-6 font-mono-tiny text-ink/60">22 autografi · 22 autographs</div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- Contact ---------- */
const Contact = ({ lang }) => {
  const t = translations[lang].contact;
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...form, language: lang });
      toast.success(t.form.success);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error(t.form.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-40 bg-bordeaux-deep text-paper relative overflow-hidden" data-testid="contact-section">
      {/* decorative marquee subtle */}
      <div className="absolute -top-6 left-0 right-0 opacity-10 pointer-events-none">
        <Marquee speed={20} gradient={false}>
          <span className="font-display italic text-[10vw] leading-none text-paper mr-16">Un cimelio irripetibile · An irreplaceable relic ·&nbsp;</span>
        </Marquee>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative">
        <div className="grid md:grid-cols-12 gap-12 md:gap-16 items-start">
          <div className="md:col-span-6">
            <div className="font-mono-tiny text-gold mb-8">
              <Reveal>{t.eyebrow}</Reveal>
            </div>
            <Reveal delay={0.05}>
              <h2 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[0.95]">
                {t.title}
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-8 text-paper/80 text-base md:text-lg leading-relaxed max-w-md">{t.body}</p>
            </Reveal>
            <Reveal delay={0.25}>
              <a
                href={`mailto:${t.recipient}`}
                className="mt-8 inline-flex items-center gap-3 text-gold font-mono-tiny hover:text-paper transition-colors"
                data-testid="contact-email-link"
              >
                <Mail className="w-4 h-4" /> {t.recipient}
              </a>
            </Reveal>
          </div>

          <div className="md:col-span-6">
            <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
              {[
                { name: "name", type: "text", label: t.form.name, tid: "contact-input-name" },
                { name: "email", type: "email", label: t.form.email, tid: "contact-input-email" },
                { name: "phone", type: "tel", label: t.form.phone, tid: "contact-input-phone" },
              ].map((f, i) => (
                <Reveal key={f.name} delay={i * 0.05}>
                  <label className="block">
                    <span className="font-mono-tiny text-paper/50 block mb-2">{f.label}</span>
                    <input
                      required
                      type={f.type}
                      name={f.name}
                      value={form[f.name]}
                      onChange={onChange}
                      className="field-underline text-paper placeholder:text-paper/30"
                      style={{ borderBottomColor: "rgba(245,241,230,0.25)", color: "#F5F1E6" }}
                      data-testid={f.tid}
                    />
                  </label>
                </Reveal>
              ))}
              <Reveal delay={0.2}>
                <label className="block">
                  <span className="font-mono-tiny text-paper/50 block mb-2">{t.form.message}</span>
                  <textarea
                    required
                    name="message"
                    rows={4}
                    value={form.message}
                    onChange={onChange}
                    className="field-underline resize-none text-paper placeholder:text-paper/30"
                    style={{ borderBottomColor: "rgba(245,241,230,0.25)", color: "#F5F1E6" }}
                    data-testid="contact-input-message"
                  />
                </label>
              </Reveal>
              <Reveal delay={0.28}>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="group inline-flex items-center gap-3 bg-gold text-bordeaux-deep hover:bg-paper transition-colors px-8 py-4 rounded-full disabled:opacity-60"
                >
                  <span className="font-mono-tiny">{submitting ? t.form.submitting : t.form.submit}</span>
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </Reveal>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- Footer ---------- */
const Footer = ({ lang }) => {
  const t = translations[lang].footer;
  return (
    <footer className="bg-ink text-paper/80 py-16 md:py-20" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-8 items-end">
        <div className="md:col-span-6">
          <div className="font-mono-tiny text-gold mb-4">{t.tag}</div>
          <div className="font-display italic text-3xl md:text-5xl text-paper leading-tight">
            {t.note}
          </div>
        </div>
        <div className="md:col-span-3 md:col-start-8 font-mono-tiny">
          <div className="text-paper/50 mb-2">Location</div>
          <div>{t.city}</div>
        </div>
        <div className="md:col-span-3 font-mono-tiny">
          <div className="text-paper/50 mb-2">Contact</div>
          <a href="mailto:francesco@cynopia.com" className="text-gold hover:text-paper transition-colors" data-testid="footer-email">francesco@cynopia.com</a>
        </div>
        <div className="md:col-span-12 mt-8 pt-6 border-t border-paper/10 flex items-center justify-between font-mono-tiny text-paper/40">
          <span>© 2026 · Cimelio Privato</span>
          <span>1 / 1</span>
        </div>
      </div>
    </footer>
  );
};

/* ---------- Loading intro ---------- */
const Intro = ({ lang, onDone }) => {
  useEffect(() => {
    const t = setTimeout(onDone, 1600);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <motion.div
      initial={{ y: 0 }}
      animate={{ y: 0 }}
      exit={{ y: "-100%" }}
      transition={{ duration: 1.2, ease: [0.85, 0, 0.15, 1] }}
      className="fixed inset-0 z-[100] bg-bordeaux-deep text-paper flex items-end p-8 md:p-12"
      data-testid="intro-overlay"
    >
      <div className="w-full flex items-end justify-between font-mono-tiny">
        <div>
          <MaskLine delay={0.1} className="text-gold">AS ROMA</MaskLine>
          <MaskLine delay={0.2}>1982 / 83</MaskLine>
        </div>
        <div className="text-right">
          <MaskLine delay={0.3}>{lang === "it" ? "Un cimelio unico" : "A one-of-one relic"}</MaskLine>
          <MaskLine delay={0.4} className="text-gold">Roma · Italia</MaskLine>
        </div>
      </div>
    </motion.div>
  );
};

/* ---------- App ---------- */
export default function AsRomaLanding() {
  useLenis();
  const [lang, setLang] = useState("it");
  const [intro, setIntro] = useState(true);

  useEffect(() => {
    setLang(detectLanguage());
  }, []);

  return (
    <div className="paper-grain relative" data-testid="app-root">
      <Toaster
        position="bottom-center"
        toastOptions={{
          style: {
            background: "#1A1515",
            color: "#F5F1E6",
            border: "1px solid rgba(240,188,66,0.3)",
            fontFamily: "Manrope, sans-serif",
            fontSize: "13px",
            letterSpacing: "0.05em",
          },
        }}
      />
      <AnimatePresence>{intro && <Intro key="intro" lang={lang} onDone={() => setIntro(false)} />}</AnimatePresence>
      <Header lang={lang} setLang={setLang} />
      <main>
        <Hero lang={lang} />
        <MarqueeStrip lang={lang} />
        <Manifesto lang={lang} />
        <Gallery lang={lang} />
        <InteractivePhoto lang={lang} />
        <Signatures lang={lang} />
        <Contact lang={lang} />
      </main>
      <Footer lang={lang} />
    </div>
  );
}
